import { z } from 'zod';

export const serviceSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  category: z.string(),
  description: z.string(),
  duration_minutes: z.number(),
  price_cents: z.number(),
  price_is_from: z.boolean(),
  is_popular: z.boolean(),
});

export const photoSchema = z.object({
  id: z.string(),
  kind: z.enum(['hero', 'logo', 'work']),
  position: z.number(),
  url: z.string(),
  caption: z.string(),
  updated_at: z.string().optional(),
});

export const tenantSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  status: z.enum(['preview', 'live', 'archived']),
  name: z.string(),
  short_name: z.string(),
  tagline: z.string(),
  description: z.string(),
  timezone: z.string(),
  currency: z.string(),
  locale: z.string(),
  accent: z.string(),
  phone: z.string(),
  address: z.string(),
  map_url: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  slot_step_minutes: z.number(),
  min_lead_minutes: z.number(),
  horizon_days: z.number(),
  cancel_policy_hours: z.number(),
  assistant_enabled: z.boolean(),
  assistant_suggestions: z.array(z.string()),
  config_version: z.number(),
  services: z.array(serviceSchema),
  hours: z.array(z.object({ weekday: z.number(), opens: z.string(), closes: z.string() })),
  exceptions: z.array(
    z.object({ day: z.string(), is_closed: z.boolean(), opens: z.string().nullable(), closes: z.string().nullable(), note: z.string() }),
  ),
  info_cards: z.array(z.object({ position: z.number(), title: z.string(), body: z.string(), icon: z.string() })),
  photos: z.array(photoSchema),
});

export type Tenant = z.infer<typeof tenantSchema>;
export type Service = z.infer<typeof serviceSchema>;
export type Photo = z.infer<typeof photoSchema>;

export const slotSchema = z.object({ starts_at: z.string(), time: z.string(), available: z.boolean() });
export const availabilitySchema = z.object({
  timezone: z.string(),
  from: z.string(),
  to: z.string(),
  days: z.array(z.object({ date: z.string(), slots: z.array(slotSchema) })),
});
export type Availability = z.infer<typeof availabilitySchema>;
export type Slot = z.infer<typeof slotSchema>;

export const publicBookingSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: z.enum(['confirmed', 'arrived', 'completed', 'cancelled', 'no_show']),
  service_id: z.string(),
  service_name: z.string(),
  starts_at: z.string(),
  ends_at: z.string(),
  duration_minutes: z.number(),
  price_cents: z.number(),
  price_is_from: z.boolean(),
  currency: z.string(),
  timezone: z.string(),
  customer_name: z.string(),
  customer_phone: z.string(),
  car: z.string(),
  comment: z.string(),
  is_demo: z.boolean(),
  reminder_opt_in: z.boolean(),
  cancelled_at: z.string().nullable(),
  cancel_until: z.string(),
  can_cancel: z.boolean(),
  replayed: z.boolean().optional(),
  tenant: z.object({ slug: z.string(), name: z.string(), phone: z.string(), address: z.string(), map_url: z.string() }),
});
export type PublicBooking = z.infer<typeof publicBookingSchema>;

export type BookingStatus = PublicBooking['status'];

export interface OwnerPayment {
  id: string;
  kind: 'payment' | 'refund';
  amount_cents: number;
  method: 'cash' | 'card' | 'transfer' | 'other';
  note: string;
  received_at: string;
}

export interface OwnerBooking {
  id: string;
  code: string;
  status: BookingStatus;
  service_id: string;
  service_name: string;
  resource_id: string;
  resource_name: string;
  starts_at: string;
  ends_at: string;
  occupied_until: string;
  duration_minutes: number;
  buffer_minutes: number;
  price_cents: number;
  price_is_from: boolean;
  customer_name: string;
  customer_phone: string;
  car: string;
  comment: string;
  source: 'client' | 'owner' | 'seed';
  is_demo: boolean;
  reschedule_count: number;
  arrived_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string;
  created_at: string;
  paid_cents: number;
  payments: OwnerPayment[];
  access_token?: string;
}

export interface Agenda {
  timezone: string;
  from: string;
  to: string;
  resources: { id: string; name: string; is_active: boolean }[];
  bookings: OwnerBooking[];
  blocks: { id: string; resource_id: string; starts_at: string; ends_at: string; reason: string }[];
  closed_days: { day: string; note: string }[];
}

export interface Stats {
  period: string;
  label: string;
  from_date: string;
  to_date: string;
  currency: string;
  bookings_scheduled: number;
  arrivals: number;
  completed_orders: number;
  completed_value_cents: number;
  payments_received_cents: number;
  refunds_cents: number;
  net_received_cents: number;
  cancellations: number;
  no_shows: number;
  upcoming_value_cents: number;
  has_demo_data: boolean;
}

export interface OwnerService {
  id: string;
  key: string;
  name: string;
  category: string;
  description: string;
  duration_minutes: number;
  buffer_minutes: number;
  price_cents: number;
  price_is_from: boolean;
  is_active: boolean;
  is_popular: boolean;
  position: number;
  resource_ids: string[];
}

export interface OwnerSettings extends Omit<Tenant, 'services' | 'exceptions'> {
  services: OwnerService[];
  resources: { id: string; key: string; name: string; is_active: boolean; position: number }[];
  exceptions: { id: number; day: string; is_closed: boolean; opens: string | null; closes: string | null; note: string }[];
}

export interface Membership {
  tenant_id: string;
  slug: string;
  name: string;
  status: string;
  role: 'owner' | 'staff';
}
